import React, {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useState,
} from "react"
import {
  EducationalRecord,
  EducationalRecordCredentials,
  EmploymentRecord,
  EmploymentRecordCredentials,
  Form,
  OLevelResult,
  OLevelResultCredentials,
  SubjectInterface,
  UploadRecordCredentials,
  stageLevel,
  stages,
  useAppNotificationStore,
  useApplicationStore,
  useAuthStore,
  useProfileStore,
} from "stores"
import {
  Button,
  ChooseFile,
  H4,
  H6,
  IconButton,
  Input,
  Label,
  ModalPortal,
  P1,
  P2,
  SelectInput,
  SelectOption,
  Table,
  TableData,
  TableHeader,
  TextAnchor,
  TextButton,
} from "components"
import { AddCircle, Eye, EyeSlash, Maximize4, Trash } from "iconsax-react"
import { images } from "assets"
import { useNavigate } from "react-router-dom"
import { BASEURL, throttle } from "utils"

export default function EducationalRecordComp() {
  const { setDashboardContext, toast } = useAppNotificationStore()
  const navigate = useNavigate()
  const { profile } = useProfileStore()
  const [educationalRecordForm] = useState(
    new Form<EducationalRecordCredentials>()
  )
  const [employmentRecordForm] = useState(
    new Form<EmploymentRecordCredentials>()
  )
  const [uploadForm] = useState(new Form<UploadRecordCredentials>())
  const {
    getQualification,
    getDateRange,
    // uploadFile,
    addEducationalRecord,
    getEducationalRecords,
    deleteEducationalRecord,

    qualifications,
    dateRanges,
    educationalRecords,
    oLevelResults,

    employmentRecords,
  } = useApplicationStore()

  const [educationalRecordsUploads, setEducationalRecordsUploads] =
    useState<EducationalRecord[]>()

  useEffect(() => {
    setDashboardContext("Records")
    const type = "EducationalRecord"
    uploadForm.setCredential("documentType", type)
    setEducationalRecordsUploads(educationalRecords!)
  }, [
    educationalRecords,
    employmentRecords,
    oLevelResults,
    profile?.nextStage,
    setDashboardContext,
    uploadForm,
  ])

  const initializePage = useCallback(async () => {
    try {
      Promise.all([await getQualification(), await getDateRange()]).then(
        (_) => {
          educationalRecordForm.setCredential(
            "qualification",
            qualifications[0]
          )
          educationalRecordForm.setCredential(
            "startDate",
            dateRanges.startDate[0]
          )
          educationalRecordForm.setCredential("endDate", dateRanges.endDate[0])
        }
      )
      await getEducationalRecords()
    } catch (error) {
      console.log(error)
      toast.error("Unable to get qualifications")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast, educationalRecordForm])

  useEffect(() => {
    return throttle(() => {
      initializePage()
    })
  }, [initializePage])

  const handleAddEducationalRecord = async (e: FormEvent) => {
    e.preventDefault()
    educationalRecordForm.submitting(true)

    try {
      educationalRecordForm.submitting(false)
      await addEducationalRecord({
        ...educationalRecordForm.credentials,
        // name: educationalRecordForm.credentials.qualification,
      }).then((resp) => {
        if (resp.isSuccessful === true) {
          toast.success(resp.message)
          educationalRecordForm.setCredentials({
            startDate: dateRanges.startDate[0],
            endDate: dateRanges.endDate[0],
            grade: "",
            qualifications: qualifications[0],
          })
        } else {
          toast.error(resp.message)
        }
      })
    } catch (error: any) {
      educationalRecordForm.submitting(false)
      console.log(error)
      toast.error(error?.message as string)
    }
  }

  return (
    <>
      <form
        onSubmit={handleAddEducationalRecord}
        encType="multipart/form-data"
        className="w-full lg:w-[536px] flex flex-col gap-8"
      >
        <div className="space-y-[5px] w-full">
          <Label
            htmlFor="qualification"
            aria-required
          >
            Select Qualification
          </Label>
          <SelectInput
            id="qualification"
            required
            disabled={educationalRecordForm.isSubmitting}
            onChange={(e) =>
              educationalRecordForm.setCredential(
                "qualification",
                e.currentTarget.value
              )
            }
          >
            {qualifications.map((option, index) => (
              <SelectOption
                key={index}
                value={option}
              >
                {option}
              </SelectOption>
            ))}
          </SelectInput>
        </div>
        {/* TODO:: Change Enter Grade to  */}
        <div className="space-y-[5px] w-full">
          <Label
            htmlFor="grade"
            aria-required
          >
            School Name
          </Label>
          <Input
            id="grade"
            required
            disabled={educationalRecordForm.isSubmitting}
            placeholder="Enter school name"
            onChange={(e) =>
              educationalRecordForm.setCredential("name", e.currentTarget.value)
            }
          />
        </div>

        <div className="space-y-[5px] w-full flex justify-between items-center gap-3">
          <div className="w-full">
            <Label
              htmlFor="start-date"
              aria-required
            >
              Start Date
            </Label>
            <SelectInput
              id="start-date"
              required
              disabled={educationalRecordForm.isSubmitting}
              onChange={(e) =>
                educationalRecordForm.setCredential(
                  "startDate",
                  e.currentTarget.value
                )
              }
            >
              {dateRanges.startDate.map((option, index) => (
                <SelectOption
                  key={index}
                  value={option}
                >
                  {option}
                </SelectOption>
              ))}
            </SelectInput>
          </div>
          <div className="w-full">
            <Label
              htmlFor="end-date"
              aria-required
            >
              End Date
            </Label>
            <SelectInput
              id="end-date"
              required
              disabled={educationalRecordForm.isSubmitting}
              onChange={(e) =>
                educationalRecordForm.setCredential(
                  "endDate",
                  e.currentTarget.value
                )
              }
            >
              {dateRanges.endDate.map((option, index) => (
                <SelectOption
                  key={index}
                  value={option}
                >
                  {option}
                </SelectOption>
              ))}
            </SelectInput>
          </div>
        </div>

        <>
          <div className="flex justify-end">
            <Button
              type="submit"
              title={"Add Record"}
              color="primary"
              className="w-52"
              disabled={educationalRecordForm.isSubmitting}
              submitting={educationalRecordForm.isSubmitting ? "true" : "false"}
            >
              Add Record
            </Button>
          </div>
          <H6 className="text-accentText font-semibold">Records</H6>
        </>
      </form>

      <div className="flex flex-col gap-0.5 w-full lg:w-[536px]">
        {educationalRecordsUploads?.length === 0 ? (
          <P1 className="text-accentGrey1">No school records yet.</P1>
        ) : (
          <Table
            cols={5}
            headers={[
              <TableHeader>#</TableHeader>,
              <TableHeader>Qualification</TableHeader>,
              <TableHeader>Start</TableHeader>,
              <TableHeader>End</TableHeader>,
              <TableHeader>Action</TableHeader>,
            ]}
            rows={educationalRecords.map((record, index) => ({
              data: [
                <TableData key={index}>{index + 1}</TableData>,
                <TableData key={index}>{record.qualification}</TableData>,
                <TableData key={index}>{record.startDate}</TableData>,
                <TableData key={index}>{record.endDate}</TableData>,
                <TextButton
                  type="button"
                  title="Delete"
                  className="flex gap-1 items-center justify-center text-stateError"
                  onClick={async () => {
                    await deleteEducationalRecord({
                      educationalRecordId: record.educationalRecId,
                    }).then((resp) => {
                      if (resp.isSuccessful === true) {
                        toast.success(resp.message)
                      } else {
                        toast.error(resp.message)
                      }
                    })
                  }}
                >
                  <Trash size={18} />
                </TextButton>,
              ],
            }))}
          />
        )}
      </div>
    </>
  )
}
